
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/base/Header';
import Footer from '../../components/base/Footer';

interface OrderData {
  id: string;
  items: any[];
  customer: any;
  total: number;
  paymentMethod: string;
  paymentLink: string;
  createdAt: string;
}

const OrderConfirmedPage = () => {
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState<OrderData | null>(null);

  useEffect(() => {
    const savedOrder = localStorage.getItem('dluxmen-last-order');
    if (savedOrder) {
      setOrderData(JSON.parse(savedOrder));
    } else {
      navigate('/');
    }
  }, [navigate]);

  const handlePayNow = () => {
    if (orderData?.paymentLink) {
      window.open(orderData.paymentLink, '_blank');
    }
  };

  const formatTotal = (total: number) => {
    return `R$ ${total.toFixed(2).replace('.', ',')}`;
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method) {
      case 'pix': return 'PIX';
      case 'credit': return 'Cartão de Crédito';
      case 'debit': return 'Cartão de Débito';
      default: return method;
    }
  };

  if (!orderData) {
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F8F8' }}>
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            {/* Success Icon */}
            <div 
              className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#0F3F2F' }}
            >
              <i className="ri-check-line text-4xl text-white"></i>
            </div>

            {/* Success Message */}
            <h1 
              className="text-3xl font-bold mb-4"
              style={{ 
                fontFamily: 'Cinzel Decorative, serif',
                color: '#0B0B0B'
              }}
            >
              ✅ Pedido Criado com Sucesso!
            </h1>
            
            <p 
              className="text-lg mb-2"
              style={{ 
                color: '#0B0B0B',
                fontFamily: 'Montserrat, sans-serif'
              }}
            >
              Pedido #{orderData.id}
            </p>
            
            <p 
              className="text-sm mb-8"
              style={{ 
                color: '#C0C0C0',
                fontFamily: 'Montserrat, sans-serif'
              }}
            >
              Clique no botão abaixo para finalizar o pagamento de forma segura
            </p>

            {/* Payment Button */}
            <button
              onClick={handlePayNow}
              className="w-full max-w-md mx-auto py-4 px-8 text-white font-bold text-lg rounded-lg transition-all duration-300 hover:bg-green-700 mb-8"
              style={{ 
                backgroundColor: '#0F3F2F',
                fontFamily: 'Montserrat, sans-serif'
              }}
            >
              Pagar Agora - {formatTotal(orderData.total)}
            </button>

            {/* Order Details */}
            <div 
              className="bg-white rounded-lg p-6 shadow-sm border text-left mb-8"
              style={{ borderColor: '#C0C0C0' }}
            >
              <h2 
                className="text-xl font-bold mb-4"
                style={{ 
                  fontFamily: 'Playfair Display, serif',
                  color: '#0B0B0B'
                }}
              >
                Detalhes do Pedido
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span 
                    className="text-sm"
                    style={{ 
                      color: '#C0C0C0',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    Cliente:
                  </span>
                  <span 
                    className="text-sm font-medium"
                    style={{ 
                      color: '#0B0B0B',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    {orderData.customer.name}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span 
                    className="text-sm"
                    style={{ 
                      color: '#C0C0C0',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    Email:
                  </span>
                  <span 
                    className="text-sm font-medium"
                    style={{ 
                      color: '#0B0B0B',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    {orderData.customer.email}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span 
                    className="text-sm"
                    style={{ 
                      color: '#C0C0C0',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    Forma de Pagamento:
                  </span>
                  <span 
                    className="text-sm font-medium"
                    style={{ 
                      color: '#0B0B0B',
                      fontFamily: 'Montserrat, sans-serif'
                    }}
                  >
                    {getPaymentMethodLabel(orderData.paymentMethod)}
                  </span>
                </div>
              </div>

              {/* Items */}
              <div 
                className="border-t pt-4"
                style={{ borderColor: '#C0C0C0' }}
              >
                <h3 
                  className="text-sm font-bold mb-3"
                  style={{ 
                    color: '#0B0B0B',
                    fontFamily: 'Montserrat, sans-serif'
                  }}
                >
                  Itens do Pedido:
                </h3>
                <div className="space-y-2">
                  {orderData.items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span 
                        style={{ 
                          color: '#C0C0C0',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        {item.quantity}x {item.brand} - {item.name}
                      </span>
                      <span 
                        style={{ 
                          color: '#0B0B0B',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        {item.price}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Additional Actions */}
            <div className="space-y-4">
              <p 
                className="text-sm"
                style={{ 
                  color: '#C0C0C0',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                Após o pagamento, você receberá a confirmação por email.
                <br />
                Dúvidas? Entre em contato conosco.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-300"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Voltar ao Início
                </button>
                
                <button
                  onClick={() => navigate('/masculino')}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-300"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  Continuar Comprando
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default OrderConfirmedPage;
